import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useGetStoreSalesQuery, useDeleteStoreSaleMutation } from '../../slices/storeSalesApiSlice';
import { useGetOpeningStoreStocksQuery } from '../../slices/openingStoreStocksApiSlice'; // For qty and itemCost
import { useGetStoreSuppliesQuery } from '../../slices/storeSuppliesApiSlice'; // For Pqty and Pcost
import { toast } from 'react-toastify';
import { useState } from 'react';
import * as XLSX from "xlsx";

const StoreStockReport = () => {
  const [filter, setFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: storeSalesData, isLoading, error, refetch } = useGetStoreSalesQuery(pageNumber);
  const { data: openingStoreStockData } = useGetOpeningStoreStocksQuery();
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery();

  const [deleteStoreSale, { isLoading: loadingDelete }] = useDeleteStoreSaleMutation();

  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this storeSale?')) {
      try {
        await deleteStoreSale(id).unwrap();
        toast.success('StoreSale deleted successfully!');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete storeSale.');
      }
    }
  };

  const filterStoreSales = () => {
    if (!storeSalesData?.storeSales) return [];
    return storeSalesData.storeSales.filter((storeSale) => {
      const storeSaleDate = new Date(storeSale.date);
      const isWithinDateRange =
        (!startDate || storeSaleDate >= new Date(startDate)) &&
        (!endDate || storeSaleDate <= new Date(endDate));
      return (
        isWithinDateRange &&
        (storeSale.note?.toLowerCase().includes(filter.toLowerCase()) ||
          storeSale.customerDetails?.name?.toLowerCase().includes(filter.toLowerCase()) ||
          storeSale.storeSaleItems.some((item) =>
            item.name.toLowerCase().includes(filter.toLowerCase())
          ))
      );
    });
  };

  const calculatePqty = (productName) => {
    return storeSuppliesData?.storeSupplies
      ?.reduce((totalQty, storeSupply) => {
        const productItems = storeSupply.storeSupplyItems.filter((item) => item.name === productName);
        const productQty = productItems.reduce((qty, item) => qty + item.qty, 0);
        return totalQty + productQty;
      }, 0) || 0;
  };

  const calculatePcost = (productName) => {
    return storeSuppliesData?.storeSupplies
      ?.reduce((totalCost, storeSupply) => {
        const productItems = storeSupply.storeSupplyItems.filter((item) => item.name === productName);
        const productCost = productItems.reduce((cost, item) => cost + item.itemCost, 0);
        return totalCost + productCost;
      }, 0) || 0;
  };

  const calculateAverageCost = (productName) => {
    const openingStoreStockItems = openingStoreStockData?.openingStoreStocks
      ?.flatMap((openingStoreStock) => openingStoreStock.openingStoreStockItems)
      ?.filter((item) => item.name === productName);

    const qty = openingStoreStockItems?.reduce((total, item) => total + item.qty, 0) || 0;
    const itemCost = openingStoreStockItems?.reduce((total, item) => total + item.itemCost, 0) || 0;

    const Pqty = calculatePqty(productName);
    const Pcost = calculatePcost(productName);

    const totalQty = qty + Pqty;
    const totalCost = itemCost + Pcost;

    return totalQty > 0 ? totalCost / totalQty : 0;
  };

  const calculateTotals = () => {
    const totalStoreSales = filterStoreSales().reduce(
      (total, storeSale) =>
        total +
        storeSale.storeSaleItems.reduce(
          (subTotal, item) => subTotal + item.qty * item.price,
          0
        ),
      0
    );

    const totalCost = filterStoreSales().reduce((total, storeSale) => {
      return total + storeSale.storeSaleItems.reduce((subTotal, item) => {
        const averageCost = calculateAverageCost(item.name);
        return subTotal + averageCost * item.qty;
      }, 0);
    }, 0);

    const totalProfit = totalStoreSales - totalCost;

    return { totalStoreSales, totalCost, totalProfit };
  };

  const { totalStoreSales, totalCost, totalProfit } = calculateTotals();

  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  const exportTableToExcel = () => {
    const tableData = filterStoreSales().map((storeSale) =>
      storeSale.storeSaleItems.map((item) => {
        const averageCost = calculateAverageCost(item.name);
        const totalCost = averageCost * item.qty;
        const profit = item.qty * item.price - totalCost;
  
        return {
          Date: new Date(storeSale.date).toLocaleDateString(),
          Product: item.name,
          Qty: item.qty,
          Price: item.price.toFixed(2),
          Total: (item.qty * item.price).toFixed(2),
          TotalCost: totalCost.toFixed(2),
          Profit: profit.toFixed(2),
          Note: storeSale.note || "N/A",
          Customer: storeSale.customerDetails?.name || "N/A",
          Company: storeSale.customerDetails?.company || "N/A",
          Telephone: storeSale.customerDetails?.telephone || "N/A",
          Serial: storeSale.customerDetails?.serialNumber || "N/A",
          Address: storeSale.customerDetails?.address || "N/A",
        };
      })
    ).flat();
  
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "StoreSales Table Export");
    XLSX.writeFile(workbook, "StoreSalesTable.xlsx");
  };

  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <h1>MiniSales</h1>
          <Button variant="success" onClick={exportTableToExcel}>Export Table to Excel</Button>
        </Col>
        
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {/* Totals Section */}
      <Row className="mb-3">
        <Col md={4}>
          <h4>Total StoreSales: ${totalStoreSales.toFixed(2)}</h4>
        </Col>
        <Col md={4}>
          <h4>Total Cost: ${totalCost.toFixed(2)}</h4>
        </Col>
        <Col md={4} className="text-end">
          <h4>Total Profit: ${totalProfit.toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>TOTAL COST</th>
                <th>PROFIT</th>
                <th>NOTE</th>
                <th>CUSTOMER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterStoreSales().map((storeSale) =>
                storeSale.storeSaleItems.map((item) => {
                  const averageCost = calculateAverageCost(item.name);
                  const totalCost = averageCost * item.qty;
                  const profit = item.qty * item.price - totalCost;

                  return (
                    <tr key={`${storeSale._id}-${item.product}`}>
                      <td>{new Date(storeSale.date).toLocaleDateString()}</td>
                      <td>{item.name}</td>
                      <td>{item.qty}</td>
                      <td>${(item.qty * item.price).toFixed(2)}</td>
                      <td>${totalCost.toFixed(2)}</td>
                      <td>${profit.toFixed(2)}</td>
                      <td>{storeSale.note || 'N/A'}</td>
                      <td>{storeSale.customerDetails.name || 'N/A'}</td>
                      <td>
                        {item.images && (
                          <img
                            src={item.images[0]}
                            alt="Thumbnail"
                            className="thumbnail-img me-2"
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '5px',
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Button
                          as={Link}
                          to={`/storeSale/${storeSale._id}`}
                          variant="light"
                          className="btn-sm"
                        >
                          Details
                        </Button>
                        <Button
                          as={Link}
                          to={`/storeSale/${storeSale._id}/edit`}
                          variant="light"
                          className="btn-sm mx-2"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          className="btn-sm mx-2"
                          onClick={() => deleteHandler(storeSale._id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(storeSalesData.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StoreStockReport;
